/**
 * CR-7275 — is this line's order price above, on, or below its contract?
 *
 * Sarah's rule, confirmed 10 Sep 2026, and it is strict: there is no
 * threshold and no tolerance.
 *
 *   unit price GREATER than contract price   over    red
 *   unit price EXACTLY EQUAL to contract     exact   green
 *   unit price LOWER than contract price     under   amber
 *
 * It does not vary by site and there is nothing to configure — an earlier
 * draft of the ticket proposed a tunable 5% band held in Admin Setup, and
 * that was withdrawn.
 *
 * One verdict drives both the contract price boxes and the PPV box. PPV is
 * `(unit_price - contracted) x quantity` (purchasing/services/ppv_service.py),
 * so it is this same comparison already multiplied out: positive means we
 * paid over contract, which is the red case. Deriving both from here rather
 * than reading PPV's sign separately keeps the box and the prices beside it
 * from ever disagreeing — and it avoids PPV's 2dp rounding turning a real
 * difference into an apparent exact match.
 *
 * Both operands come from the same ContractPricePart: the screen's
 * `contract_unit_price` is `contract_price_part.unit_price`, and PPV's
 * `contracted` comes from `PPVService.get_contracted_price`, which resolves
 * that same row. So there is one number being compared, not two that happen
 * to look alike.
 */

/** Where the order price sits against the contract, or null if not comparable. */
export type ContractPriceVariance = 'over' | 'exact' | 'under';

/** A decimal string split into sign and scaled integer digits. */
interface ScaledDecimal {
  negative: boolean;
  digits: string;
  scale: number;
}

const DECIMAL_PATTERN = /^\s*([+-]?)(\d*)(?:\.(\d*))?\s*$/;

function parseDecimal(value: string): ScaledDecimal | null {
  const match = DECIMAL_PATTERN.exec(value);
  if (!match) return null;
  const [, sign, whole = '', fraction = ''] = match;
  if (!whole && !fraction) return null;
  return {
    negative: sign === '-',
    digits: `${whole || '0'}${fraction}`,
    scale: fraction.length,
  };
}

/**
 * Compares two decimal strings exactly, without going through a float.
 *
 * "Exact means exact" is the whole point of the green band, and both operands
 * are DecimalFields serialised as strings, so they arrive at whatever scale
 * the column was declared with: `349.59` and `349.590` are the same price and
 * have to compare equal. Parsing to `number` would mostly work and then
 * quietly not — 0.1 + 0.2 arithmetic is the reason the ticket says never
 * through a float — so both sides are scaled to a common number of decimal
 * places and compared as BigInt.
 *
 * @returns -1, 0 or 1 as with any comparator, or null if either side is not a
 *   decimal we can read.
 */
export function compareDecimalStrings(a: string, b: string): -1 | 0 | 1 | null {
  const left = parseDecimal(a);
  const right = parseDecimal(b);
  if (!left || !right) return null;

  const scale = Math.max(left.scale, right.scale);
  const scaled = (d: ScaledDecimal) => {
    const padded = d.digits + '0'.repeat(scale - d.scale);
    // `-0` and `0` are the same number; normalise before applying the sign.
    const magnitude = BigInt(padded);
    return d.negative ? -magnitude : magnitude;
  };

  const l = scaled(left);
  const r = scaled(right);
  if (l < r) return -1;
  if (l > r) return 1;
  return 0;
}

/** The fields this needs — satisfied by PurchaseOrderLine. */
export interface ContractPricedLine {
  unit_price?: string | null;
  contract_unit_price?: string | null;
  /** Null exactly when the line resolved to no ContractPricePart. */
  contract_id?: number | null;
}

/**
 * Whether a contract was resolved for this line at all.
 *
 * `contract_id` is serialised from `contract_price_part.contract_id`
 * (CR-7266), so it is null on precisely the lines with no contract part —
 * 99.65% of the book at the time of writing. Those get the empty state and no
 * colour, rather than being coloured as though they were on contract.
 */
export function hasContract(line: ContractPricedLine): boolean {
  return line.contract_id != null;
}

/**
 * The three-way verdict for a line, or null when there is nothing to compare.
 *
 * Null covers a line with no contract, and a line whose prices are missing or
 * unreadable. A null verdict must render no colour at all.
 */
export function getContractPriceVariance(
  line: ContractPricedLine,
): ContractPriceVariance | null {
  if (!hasContract(line)) return null;
  const ordered = line.unit_price;
  const contracted = line.contract_unit_price;
  if (!ordered || !contracted) return null;

  const comparison = compareDecimalStrings(ordered, contracted);
  if (comparison === null) return null;
  if (comparison > 0) return 'over';
  if (comparison < 0) return 'under';
  return 'exact';
}

/**
 * Text colour for a verdict. Null verdict means no class, so a line with
 * nothing to compare stays visually neutral.
 */
export function varianceTextClass(variance: ContractPriceVariance | null): string {
  if (variance === 'over') return 'text-red-700 dark:text-red-400';
  if (variance === 'under') return 'text-amber-700 dark:text-amber-400';
  if (variance === 'exact') return 'text-green-700 dark:text-green-400';
  return '';
}

/** Shown in place of a blank, so an empty box reads as explained, not broken. */
export const NO_CONTRACT_TEXT = 'No contract';

/**
 * Contract parts are priced per unit or per lot, never both
 * (purchasing/models/contract.py, enforced by the importer), so on a
 * unit-priced part the lot box is empty by design and says so instead of
 * taking a colour it could never earn.
 */
export const LOT_NOT_APPLICABLE_TEXT = 'Not applicable, unit priced';
